import _ from 'lodash'
import moment from 'moment'
import { MONTHS_OF, money, plural } from '@/helpers'

const WEEKDAYS = ['понедельник', 'вторник', 'среда', 'четверг', 'пятница', 'суббота', 'воскресенье']

const WEEKDAYS_SHORT = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс']

const WEEKDAYS_AT = [
  'в понедельник', 'во вторник', 'в среду', 'в четверг', 'в пятницу', 'в субботу', 'в воскресенье',
]

const WEEKDAYS_TO_LAST = [
  'к прошлому понедельнику', 'к прошлому вторнику', 'к прошлой среде', 'к прошлому четвергу',
  'к прошлой пятнице', 'к прошлой субботе', 'к прошлому воскресенью',
]

const HOURS = _.range(24)

const ORDERS = ['заказ', 'заказа', 'заказов']

const isNumber = value => typeof value === 'number' && Number.isFinite(value)

const numberOrNull = value => (isNumber(value) ? value : null)

const isObject = value => _.isPlainObject(value)

const dayOrNull = value => {
  const date = typeof value === 'string' ? moment(value, moment.ISO_8601, true) : null

  return date && date.isValid() ? date.format('YYYY-MM-DD') : null
}

const number = (value, digits = 0) => new Intl.NumberFormat('ru-RU', { maximumFractionDigits: digits }).format(value)

export const toCountString = value => (value === null ? '—' : number(value))

export const toPercentString = value => (value === null ? '—' : `${number(value, 1)}%`)

export const toMinutesString = value => (value === null ? '—' : `${number(value, 1)} мин`)

export const toOrdersString = value => `${number(value, 1)} ${plural(_.round(value, 1), ORDERS)}`

export const toHourString = hour => `${String(hour).padStart(2, '0')}:00`

export const toDayMonthString = value => {
  const date = moment(value)

  return `${date.date()} ${MONTHS_OF[date.month()]}`
}

export const toWeekdayDayString = value => `${WEEKDAYS[moment(value).isoWeekday() - 1]}, ${toDayMonthString(value)}`

export const toShortWeekdayDayString = value => (
  `${WEEKDAYS_SHORT[moment(value).isoWeekday() - 1].toLowerCase()}, ${toDayMonthString(value)}`
)

export const toPeriodString = (from, to) => {
  if (!from || !to) return ''
  if (from === to) return toDayMonthString(from)

  return moment(from).isSame(to, 'month')
    ? `${moment(from).date()}–${toDayMonthString(to)}`
    : `${toDayMonthString(from)} — ${toDayMonthString(to)}`
}

export const toUpdatedAtString = value => {
  const date = typeof value === 'string' ? moment(value, moment.ISO_8601, true) : null

  return date && date.isValid() ? date.format('HH:mm') : ''
}

const isWeekend = value => moment(value).isoWeekday() >= 6

const figures = raw => ({
  date: dayOrNull(raw.date),
  orders: numberOrNull(raw.orders),
  revenue: numberOrNull(raw.revenue),
  averageCheck: numberOrNull(raw.averageCheck),
  cancelled: numberOrNull(raw.cancelled),
  cancelledShare: numberOrNull(raw.cancelledShare),
  reentered: numberOrNull(raw.reentered),
})

const todayBlock = raw => (isObject(raw)
  ? { ...figures(raw), previous: isObject(raw.previous) ? figures(raw.previous) : null }
  : null)

const relativeChange = (current, previous) => (current === null || !previous
  ? null
  : ((current - previous) / previous) * 100)

const pointsChange = (current, previous) => (current === null || previous === null ? null : current - previous)

const changeView = (value, unit, upIsGood, toLast) => {
  if (value === null) return null

  const rounded = _.round(value, Math.abs(value) < 10 ? 1 : 0)

  if (rounded === 0) {
    return {
      direction: 0, tone: 'flat', value: 'без изменений', text: toLast,
    }
  }

  const up = rounded > 0

  return {
    direction: up ? 1 : -1,
    tone: up === upIsGood ? 'good' : 'bad',
    value: `${up ? '+' : '−'}${number(Math.abs(rounded), 1)}${unit}`,
    text: toLast,
  }
}

const REENTERED_TOOLTIP = 'Колл-центр отменил заказ в Delivery и завёл его повторно — продажа состоялась'

const cancelledTile = (today, previous, toLast) => {
  const isMissing = today.cancelledShare === null

  return {
    title: 'Отменено',
    value: isMissing ? 'нет данных' : toPercentString(today.cancelledShare),
    isMissing,
    note: isMissing || today.cancelled === null ? '' : toOrdersString(today.cancelled),
    change: changeView(pointsChange(today.cancelledShare, previous.cancelledShare), ' п. п.', false, toLast),
    extra: today.reentered > 0
      ? {
        text: `ещё ${toCountString(today.reentered)} ${plural(today.reentered, ['заведён', 'заведены', 'заведены'])} заново`,
        tooltip: REENTERED_TOOLTIP,
      }
      : null,
  }
}

export const todayTiles = today => {
  const previous = today.previous || figures({})
  const toLast = previous.date ? WEEKDAYS_TO_LAST[moment(previous.date).isoWeekday() - 1] : 'к прошлой неделе'
  const noOrders = today.orders === 0

  return [
    {
      title: 'Заказов',
      value: toCountString(today.orders),
      note: '',
      change: changeView(relativeChange(today.orders, previous.orders), '%', true, toLast),
    },
    {
      title: 'Выручка',
      value: money(today.revenue),
      note: '',
      change: changeView(relativeChange(today.revenue, previous.revenue), '%', true, toLast),
    },
    {
      title: 'Средний чек',
      value: money(today.averageCheck),
      note: today.averageCheck === null && noOrders ? 'заказов ещё не было' : '',
      change: changeView(relativeChange(today.averageCheck, previous.averageCheck), '%', true, toLast),
    },
    cancelledTile(today, previous, toLast),
  ]
}

const isCell = cell => isObject(cell)
  && Number.isInteger(cell.weekday) && cell.weekday >= 1 && cell.weekday <= 7
  && Number.isInteger(cell.hour) && cell.hour >= 0 && cell.hour <= 23
  && isNumber(cell.orders) && cell.orders >= 0

const hourlyBlock = raw => (isObject(raw) && Array.isArray(raw.cells)
  ? {
    from: dayOrNull(raw.from),
    to: dayOrNull(raw.to),
    weeks: numberOrNull(raw.weeks),
    cells: raw.cells.filter(isCell),
  }
  : null)

const cellTooltip = (weekday, hour, orders) => {
  const at = `${WEEKDAYS_SHORT[weekday - 1]} ${toHourString(hour)}`

  if (orders === null) return `${at} — нет данных`

  return orders > 0 ? `${at} — в среднем ${toOrdersString(orders)}` : `${at} — заказов не было`
}

export const heatMap = cells => {
  const grid = WEEKDAYS.map(() => HOURS.map(() => null))

  cells.forEach(({ weekday, hour, orders }) => { grid[weekday - 1][hour] = orders })

  const busyHours = HOURS.filter(hour => grid.some(row => row[hour] > 0))

  if (!busyHours.length) return null

  const hours = _.range(Math.max(0, busyHours[0] - 1), Math.min(23, _.last(busyHours) + 1) + 1)
  const max = _.max(cells.map(cell => cell.orders))

  return {
    hours,
    rows: grid.map((row, index) => ({
      weekday: index + 1,
      name: WEEKDAYS_SHORT[index],
      cells: hours.map(hour => ({
        hour,

        level: row[hour] ? row[hour] / max : 0,
        tooltip: cellTooltip(index + 1, hour, row[hour]),
      })),
    })),
  }
}

export const heatSummary = cells => {
  const peak = _.maxBy(cells, 'orders')

  if (!peak || !peak.orders) return ''

  const weekdayHours = HOURS
    .map(hour => ({ hour, orders: _.meanBy(cells.filter(cell => cell.weekday <= 5 && cell.hour === hour), 'orders') }))
    .filter(item => item.orders > 0)
  const weekdayPeak = _.maxBy(weekdayHours, 'orders')

  return [
    `Больше всего заказов — ${WEEKDAYS_AT[peak.weekday - 1]} в ${toHourString(peak.hour)}: `
      + `в среднем ${toOrdersString(peak.orders)}.`,
    weekdayPeak
      ? `В будни самый загруженный час — ${toHourString(weekdayPeak.hour)}: в среднем ${toOrdersString(weekdayPeak.orders)}.`
      : '',
  ].filter(x => x).join(' ')
}

const dailyBlock = raw => (Array.isArray(raw)
  ? raw
    .filter(day => isObject(day) && dayOrNull(day.date))
    .map(day => ({
      date: dayOrNull(day.date),
      orders: numberOrNull(day.orders),
      revenue: numberOrNull(day.revenue),
      isWeekend: isWeekend(day.date),
    }))
  : null)

const SPEED_FIGURES = ['toKitchenMinutes', 'cookingMinutes', 'onTheWayMinutes', 'totalMinutes', 'overHourShare']

const speedBlock = raw => {
  if (!isObject(raw)) return null

  const block = {
    from: dayOrNull(raw.from),
    to: dayOrNull(raw.to),
    orders: numberOrNull(raw.orders),
    ..._.fromPairs(SPEED_FIGURES.map(field => [field, numberOrNull(raw[field])])),
  }

  return { ...block, hasFigures: SPEED_FIGURES.some(field => block[field] !== null) }
}

const shareRows = raw => (Array.isArray(raw)
  ? raw
    .filter(row => isObject(row) && typeof row.name === 'string')
    .map(row => ({
      key: row.key || row.name,
      name: row.name,
      orders: numberOrNull(row.orders),
      share: numberOrNull(row.share),
    }))
  : null)

const channelsBlock = raw => {
  if (!isObject(raw)) return null

  const sources = shareRows(raw.sources)
  const payments = shareRows(raw.payments)

  return sources || payments
    ? {
      from: dayOrNull(raw.from), to: dayOrNull(raw.to), sources, payments,
    }
    : null
}

const restaurantsBlock = raw => (Array.isArray(raw)
  ? raw
    .filter(row => isObject(row) && typeof row.name === 'string')
    .map((row, index) => ({
      key: index,
      name: row.name,
      orders: numberOrNull(row.orders),
      revenue: numberOrNull(row.revenue),
      averageCheck: numberOrNull(row.averageCheck),
      overHourShare: numberOrNull(row.overHourShare),

      delivered: numberOrNull(row.delivered),
    }))
  : null)

const RELIABLE_DELIVERED = 10

const hasShare = row => row.overHourShare !== null && row.delivered !== null

const isReliable = row => hasShare(row) && row.delivered >= RELIABLE_DELIVERED

export const fewDeliveriesHint = row => (hasShare(row) && !isReliable(row)
  ? `Мало доставок: ${number(row.delivered)} — доля неточная`
  : '')

const OUTLIER_RATIO = 1.5
const OUTLIER_POINTS = 5

export const worstOverHourKey = restaurants => {
  const [worst, next] = _.orderBy(restaurants.filter(isReliable), 'overHourShare', 'desc')

  if (!worst || !next) return null

  return worst.overHourShare >= next.overHourShare * OUTLIER_RATIO
    && worst.overHourShare - next.overHourShare >= OUTLIER_POINTS
    ? worst.key
    : null
}

const attentionBlock = raw => (isObject(raw)
  ? {
    paidNotInDelivery: numberOrNull(raw.paidNotInDelivery),
    awaitingPaymentTooLong: numberOrNull(raw.awaitingPaymentTooLong),
    awaitingPaymentMinutes: numberOrNull(raw.awaitingPaymentMinutes),
    webHooksFailed: numberOrNull(raw.webHooksFailed),
  }
  : null)

const safely = parse => raw => {
  try {
    return parse(raw)
  } catch {
    return null
  }
}

const parsers = {
  today: todayBlock,
  hourly: hourlyBlock,
  daily: dailyBlock,
  speed: speedBlock,
  channels: channelsBlock,
  restaurants: restaurantsBlock,
  attention: attentionBlock,
}

export const dashboardBlocks = data => _.mapValues(parsers, (parse, name) => safely(parse)(isObject(data) ? data[name] : null))
